import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { CfdPunto, ApiResponse } from '../types';

const ESTADO_COLORS: Record<string, string> = {
    backlog: '#94a3b8',
    por_hacer: '#64748b',
    en_progreso: '#3b82f6',
    esperando_cliente: '#f59e0b',
    testing: '#a855f7',
    produccion: '#10b981',
};

const ESTADO_LABELS: Record<string, string> = {
    backlog: 'Backlog',
    por_hacer: 'Por Hacer',
    en_progreso: 'En Progreso',
    esperando_cliente: 'Esperando',
    testing: 'Testing',
    produccion: 'Producción',
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const rangos = [
    { dias: 7, label: '7 días' },
    { dias: 30, label: '30 días' },
    { dias: 90, label: '90 días' },
];

export const CFDChart: React.FC = () => {
    const [dias, setDias] = useState<number>(30);
    const [data, setData] = useState<CfdPunto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await apiFetch(`${API_BASE}/tareas/cfd?dias=${dias}`);
                const json: ApiResponse<CfdPunto[]> = await res.json();
                if (json.success && json.data) setData(json.data);
                else setError(json.error || 'Error al cargar CFD');
            } catch (e) {
                setError('No se pudo conectar con el servidor');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [dias]);

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-slate-800">Diagrama de Flujo Acumulado (CFD)</h3>
                    <p className="text-xs text-slate-500">
                        Bandas paralelas = sistema estable. Banda que se ensancha = cuello de botella.
                    </p>
                </div>
                <div className="flex gap-1">
                    {rangos.map(r => (
                        <button
                            key={r.dias}
                            onClick={() => setDias(r.dias)}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                                dias === r.dias
                                    ? 'bg-slate-800 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>
            {loading && <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Cargando...</div>}
            {error && <div className="h-64 flex items-center justify-center text-red-500 text-sm">{error}</div>}
            {!loading && !error && data.length > 0 && (
                <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="fecha"
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            tickFormatter={(v: string) => v.slice(5)}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                        <Tooltip
                            contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}
                            labelFormatter={(v) => `Fecha: ${v}`}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {(['backlog', 'por_hacer', 'en_progreso', 'esperando_cliente', 'testing', 'produccion'] as const).map(estado => (
                            <Area
                                key={estado}
                                type="monotone"
                                dataKey={estado}
                                stackId="1"
                                stroke={ESTADO_COLORS[estado]}
                                fill={ESTADO_COLORS[estado]}
                                fillOpacity={0.6}
                                name={ESTADO_LABELS[estado]}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            )}
            {!loading && !error && data.length === 0 && (
                <div className="h-64 flex items-center justify-center text-slate-400 text-sm">Sin datos</div>
            )}
        </div>
    );
};
